<template>
  <div class="page-container billing-gloss billing-gloss-page">
    <portal to="page-name">Glosas</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <h1 class="card-title">Glosas</h1>
      </div>
      <div class="card-body">
        <gloss-kpis
          :totals="kpiTotals"
          :hints="kpiHints"
          :period="periodLabel"
          :period-hint="periodHint"
          highlight="toAppeal"
        />

        <div class="cr-panel cr-panel-pending cr-panel-first">
          <div class="cr-toolbar">
            <div class="cr-toolbar-field bg-field-insurance">
              <label class="form-label">Convênio</label>
              <select class="form-select" v-model="filter.planId">
                <option value="">Todos os convênios</option>
                <option v-for="item in insurances" :value="item.planId" :key="item.planId">
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div class="cr-toolbar-field"
                 :class="{'has-error': $v.filter.startDate.$error || $v.filter.endDate.$error}">
              <label class="form-label">Período da glosa</label>
              <st-date-period
                :start-date.sync="filter.startDate"
                :end-date.sync="filter.endDate"
                :periods="periods"
                :max-months="6"
                :start-date-validator="$v.filter.startDate"
                :end-date-validator="$v.filter.endDate"
                @blur:startDate="$v.filter.startDate.$touch()"
                @blur:endDate="$v.filter.endDate.$touch()"
              />
            </div>
            <div class="cr-toolbar-field bg-field-status">
              <label class="form-label">Status da glosa</label>
              <st-multi-select v-model="filter.statuses" :items="statusItems"
                               value-key="value" label-key="label"/>
            </div>
            <div class="cr-toolbar-field bg-field-code">
              <label class="form-label">Código (Tabela 38)</label>
              <gloss-code-autocomplete id="queue-gloss-code" v-model="filter.code"
                                       placeholder="1402, carteira..."/>
            </div>
            <div class="cr-toolbar-field bg-field-professional">
              <label class="form-label">Profissional</label>
              <select class="form-select" v-model="filter.professionalId">
                <option value="">Todos</option>
                <option v-for="item in professionals" :value="item.id" :key="item.id">
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div class="cr-toolbar-field cr-toolbar-grow">
              <label class="form-label">Pesquisar</label>
              <input type="text" class="form-input" v-model="filter.search"
                     placeholder="Paciente, nº guia, lote, protocolo" @keypress.enter="load">
            </div>
            <div class="cr-toolbar-actions">
              <button class="btn btn-secondary btn-icon btn-icon-left"
                      :class="{ loading }" :disabled="loading" @click="load">
                <fa-icon :icon="['fal', 'search']"/>Pesquisar
              </button>
            </div>
          </div>
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'layer-group']" class="cr-panel-icon"/>
              Lotes com glosa
              <span class="bg-count">{{ rows.length }}</span>
            </div>
            <div class="cr-panel-right text-xs text-gray">
              Ordenado por prazo do recurso, depois valor
            </div>
            <div class="cr-panel-subtitle">
              Agrupado por lote e convênio. Abra o lote para trabalhar item a item.
            </div>
          </div>
          <div class="loading loading-lg cr-panel-loading" v-if="loading"></div>
          <div class="empty cr-panel-empty" v-else-if="rows.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', hasFilter ? 'filter' : 'check-circle']" size="2x"
                       :class="{ 'bg-empty-icon-done': !hasFilter }"/>
            </div>
            <p class="empty-subtitle" v-if="hasFilter">Nenhum lote com esse filtro.</p>
            <p class="empty-subtitle" v-else>Nenhuma glosa aberta no período. Bom sinal.</p>
            <button class="btn btn-link" v-if="hasFilter" @click="clearFilters">
              Limpar filtros
            </button>
          </div>
          <div class="cr-table-scroll" v-else>
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Prazo</th>
                <th>Lote</th>
                <th>Guias / itens</th>
                <th class="text-right">Glosado</th>
                <th class="text-right hide-sm">Em recurso</th>
                <th class="text-right hide-sm">Recuperado</th>
                <th class="text-right">A recursar</th>
                <th>Status</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in rows" :key="row.batchId" class="c-hand"
                  :class="{ 'bg-row-dim': row.totals.toAppeal === 0 }"
                  @click="openBatch(row)">
                <td class="text-nowrap">
                  <deadline-clock :deadline="row.deadline"/>
                </td>
                <td>
                  <div class="text-bold">Lote {{ row.number }}</div>
                  <div class="text-xs text-gray">{{ batchMeta(row) }}</div>
                </td>
                <td class="text-nowrap">
                  {{ row.counts.guides }} {{ row.counts.guides === 1 ? 'guia' : 'guias' }}
                  · {{ row.counts.items }}
                  {{ row.counts.items === 1 ? 'item glosado' : 'itens glosados' }}
                </td>
                <td class="text-right tnum text-red">−{{ row.totals.glossed | currency }}</td>
                <td class="text-right tnum hide-sm"
                    :class="row.totals.appealed ? 'text-amber' : 'text-gray'">
                  {{ row.totals.appealed ? $options.filters.currency(row.totals.appealed) : '—' }}
                </td>
                <td class="text-right tnum hide-sm"
                    :class="row.totals.recovered ? 'text-green' : 'text-gray'">
                  {{ row.totals.recovered
                    ? `+${$options.filters.currency(row.totals.recovered)}`
                    : '—' }}
                </td>
                <td class="text-right tnum">
                  <b class="text-primary">{{ row.totals.toAppeal | currency }}</b>
                </td>
                <td>
                  <span class="bg-pill" :class="batchStatusClass(row.status)">
                    {{ batchStatusName(row.status) }}
                  </span>
                </td>
                <td class="text-right">
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          data-tooltip="Abrir glosas" @click.stop="openBatch(row)">
                    <fa-icon :icon="['fal', 'chevron-right']"/>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="cr-panel">
          <div class="cr-panel-head">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'gavel']" class="cr-panel-icon"/>
              Recursos aguardando resposta
              <span class="bg-count">{{ appeals.length }}</span>
            </div>
            <div class="cr-panel-subtitle">
              Recursos enviados sem resposta registrada. Quando chegar o demonstrativo de pagamento
              do tipo Recurso, a resposta é sugerida automaticamente.
            </div>
          </div>
          <div class="loading loading-lg cr-panel-loading" v-if="loading"></div>
          <div class="empty cr-panel-empty" v-else-if="appeals.length === 0">
            <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="2x"/></div>
            <p class="empty-subtitle">Nenhum recurso enviado aguardando resposta.</p>
          </div>
          <div class="cr-table-scroll" v-else>
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Recurso</th>
                <th>Lote</th>
                <th>Rodada</th>
                <th>Enviado em</th>
                <th class="text-right">Recursado</th>
                <th>Aguardando</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="appeal in appeals" :key="appeal.id">
                <td class="text-bold">{{ appeal.number }}</td>
                <td>
                  <template v-if="appeal.batch">
                    Lote {{ appeal.batch.number }}
                    <span class="text-gray">· {{ insuranceName(appeal.batch.insurance) }}</span>
                  </template>
                </td>
                <td><span class="bg-pill bg-pill-outline">{{ roundName(appeal.round) }}</span></td>
                <td class="text-nowrap">{{ appeal.dates.sentAt | date }}</td>
                <td class="text-right tnum">{{ appeal.totals.appealed | currency }}</td>
                <td :class="appeal.daysSinceSent > 15 ? 'text-amber' : 'text-gray'">
                  {{ waitingLabel(appeal.daysSinceSent) }}
                </td>
                <td class="text-right text-nowrap">
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          data-tooltip="Abrir lote"
                          @click="openBatch({ batchId: appeal.batch._id }, 'appeals')">
                    <fa-icon :icon="['fal', 'eye']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="canManage ? 'Registrar resposta' : 'Sem permissão'"
                          :disabled="!canManage" @click="respond(appeal)">
                    <fa-icon :icon="['fal', 'reply']"/>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="card-footer" v-if="rows.length > 0">
        Lotes: <strong class="text-primary">{{ rows.length }}</strong>
        <span class="ml-2">
          Glosado: <strong class="text-primary">{{ totals.glossed | currency }}</strong>
        </span>
        <span class="ml-2">
          A recursar: <strong class="text-primary">{{ totals.toAppeal | currency }}</strong>
        </span>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <appeal-response-modal
      v-if="response.show"
      :show="response.show"
      :appeal-id="response.appealId"
      @close="closeResponse"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate, maxDate } from '@/data/validators';
import { LIST_BILLINGS, MANAGE_BILLING_GLOSSES } from '@/data/actions/modules/financial';
import { statuses as glossStatuses } from '@/data/billing-gloss-statuses';
import { getRoundName } from '@/data/billing-appeal-statuses';
import { allActive as allActiveProfessionals } from '@/app/professional/api';
import Forbidden from '@/components/auth/Forbidden.vue';
import GlossKpis from './GlossKpis.vue';
import DeadlineClock from './DeadlineClock.vue';
import GlossCodeAutocomplete from './GlossCodeAutocomplete.vue';
import AppealResponseModal from '../modals/AppealResponseModal.vue';
import {
  batchStatuses, batchStatusClasses, guideTypes, idOf,
} from '../helpers';

const DEFAULT_STATUSES = Object.keys(glossStatuses);

function blankFilter() {
  return {
    planId: '',
    insuranceId: '',
    startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    statuses: [...DEFAULT_STATUSES],
    code: null,
    professionalId: '',
    search: '',
  };
}

export default {
  components: {
    Forbidden,
    GlossKpis,
    DeadlineClock,
    GlossCodeAutocomplete,
    AppealResponseModal,
  },
  data() {
    return {
      loading: false,
      insurances: [],
      professionals: [],
      periods: [
        { value: 'd:-30', text: 'Últimos 30 dias' },
        { value: 'd:-60', text: 'Últimos 60 dias' },
        { value: 'd:-90', text: 'Últimos 90 dias' },
        { value: 'd:-180', text: 'Últimos 180 dias' },
        { value: 'month', text: 'Este mês' },
        { value: 'month:-1', text: 'Mês passado' },
      ],
      filter: blankFilter(),
      rows: [],
      appeals: [],
      totals: {
        informed: 0,
        glossed: 0,
        appealed: 0,
        recovered: 0,
        loss: 0,
        toAppeal: 0,
        itemsToAppeal: 0,
        glossPercent: 0,
        recoveredPercent: 0,
        sentAppeals: 0,
        nextDeadline: null,
      },
      response: {
        show: false,
        appealId: null,
      },
    };
  },
  computed: {
    hasAccess() {
      return this.$can(LIST_BILLINGS);
    },
    canManage() {
      return this.$can(MANAGE_BILLING_GLOSSES);
    },
    statusItems() {
      return Object.keys(glossStatuses).map(key => ({ value: key, label: glossStatuses[key] }));
    },
    hasFilter() {
      const { filter } = this;
      return !!(filter.planId || filter.insuranceId || filter.code || filter.professionalId
        || filter.search.trim())
        || filter.statuses.length !== DEFAULT_STATUSES.length
        || !DEFAULT_STATUSES.every(status => filter.statuses.includes(status));
    },
    kpiTotals() {
      return {
        informed: this.totals.informed,
        glossed: this.totals.glossed,
        glossPercent: this.totals.glossPercent,
        appealed: this.totals.appealed,
        recovered: this.totals.recovered,
        loss: this.totals.loss,
        toAppeal: this.totals.toAppeal,
      };
    },
    kpiHints() {
      const next = this.totals.nextDeadline;
      const items = this.totals.itemsToAppeal;
      let deadline = null;
      if (next) {
        if (next.days === 0) deadline = 'próximo prazo vence hoje';
        else if (next.days === 1) deadline = 'próximo prazo vence amanhã';
        else deadline = `próximo prazo em ${next.days} dias`;
      }
      return {
        informed: `${this.rows.length} ${this.rows.length === 1 ? 'lote' : 'lotes'}`,
        appealed: `${this.totals.sentAppeals} ${this.totals.sentAppeals === 1 ? 'recurso enviado' : 'recursos enviados'}`,
        recovered: this.totals.recoveredPercent
          ? `${String(this.totals.recoveredPercent).replace('.', ',')}% do respondido`
          : null,
        loss: 'aceitas + mantidas',
        highlight: [
          `${items} ${items === 1 ? 'item' : 'itens'}`,
          deadline,
        ].filter(Boolean).join(' · '),
      };
    },
    periodLabel() {
      const start = moment(this.filter.startDate);
      const end = moment(this.filter.endDate);
      if (!start.isValid() || !end.isValid()) return '—';
      return `${start.format('DD/MM')} a ${end.format('DD/MM/YYYY')}`;
    },
    periodHint() {
      const start = moment(this.filter.startDate);
      const end = moment(this.filter.endDate);
      if (!start.isValid() || !end.isValid()) return null;
      const days = end.diff(start, 'days') + 1;
      const insurances = new Set(this.rows.map(row => idOf(row.insurance))).size;
      return `${days} dias · ${insurances} ${insurances === 1 ? 'convênio' : 'convênios'}`;
    },
  },
  validations() {
    const { startDate } = this.filter;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      filter: {
        startDate: { required, date },
        endDate: {
          required,
          date,
          minDate: minDate(validStart ? startDate : null),
          maxDate: maxDate(validStart ? moment(startDate).add(6, 'months').format('YYYY-MM-DD') : null),
        },
      },
    };
  },
  mounted() {
    if (!this.hasAccess) return;
    this.applyQuery();
    this.loadInsurances();
    this.loadProfessionals();
    this.load();
  },
  methods: {
    applyQuery() {
      const query = this.$route.query || {};
      if (moment(query.startDate, 'YYYY-MM-DD', true).isValid()) {
        this.filter.startDate = String(query.startDate);
      }
      if (moment(query.endDate, 'YYYY-MM-DD', true).isValid()) {
        this.filter.endDate = String(query.endDate);
      }
      if (query.planId) this.filter.planId = String(query.planId);
      if (query.insuranceId) this.filter.insuranceId = String(query.insuranceId);
      if (query.code) {
        this.filter.code = {
          code: String(query.code),
          description: query.codeDescription ? String(query.codeDescription) : '',
        };
      }
      if (query.code || query.insuranceId || query.planId) {
        this.filter.statuses = Object.keys(glossStatuses);
      }
    },
    load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) return Promise.resolve();

      this.loading = true;
      const params = {
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
      };
      if (this.filter.statuses.length) params.status = this.filter.statuses.join(',');
      if (this.filter.planId) params.planId = this.filter.planId;
      if (this.filter.insuranceId) params.insuranceId = this.filter.insuranceId;
      if (this.filter.code) params.code = this.filter.code.code;
      if (this.filter.professionalId) params.professionalId = this.filter.professionalId;
      if (this.filter.search.trim()) params.search = this.filter.search.trim();

      return this.$http.get('/billing-glosses/queue', { params })
        .then(({ data }) => {
          this.rows = data.items || [];
          this.appeals = data.appeals || [];
          this.totals = { ...this.totals, ...(data.totals || {}) };
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInsurances() {
      return this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.insurances = data.items
            .filter(item => item.type !== 'particular')
            .map(item => ({ planId: item.plan.id, insuranceId: item.id, name: item.customName }));
        })
        .catch(() => {});
    },
    loadProfessionals() {
      return allActiveProfessionals()
        .then((data) => {
          this.professionals = (data.items || []).map(item => ({ id: item.id, name: item.name }));
        })
        .catch(() => {});
    },
    clearFilters() {
      this.filter = blankFilter();
      this.$v.filter.$reset();
      this.load();
    },
    batchMeta(row) {
      const parts = [
        this.insuranceName(row.insurance),
        guideTypes[row.guideType] || row.guideType,
      ];
      if (row.protocol && row.protocol.number) parts.push(`protocolo ${row.protocol.number}`);
      return parts.filter(Boolean).join(' · ');
    },
    insuranceName(insurance) {
      if (!insurance) return '';
      const plan = insurance.plan && insurance.plan.name;
      return plan && plan !== insurance.name ? `${insurance.name} · ${plan}` : insurance.name;
    },
    batchStatusName(status) {
      return batchStatuses[status] || status;
    },
    batchStatusClass(status) {
      return batchStatusClasses[status] || 'bg-pill-gray';
    },
    roundName(round) {
      return getRoundName(round);
    },
    waitingLabel(days) {
      if (days === null || days === undefined) return '—';
      if (days === 0) return 'hoje';
      return `${days} ${days === 1 ? 'dia' : 'dias'}`;
    },
    openBatch(row, tab = 'glosses') {
      this.$router.push(`/financial/billing-batches/${row.batchId}?tab=${tab}`);
    },
    respond(appeal) {
      this.response = { show: true, appealId: appeal.id };
    },
    closeResponse(saved) {
      this.response = { show: false, appealId: null };
      if (saved) this.load();
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";

.billing-gloss-page {
  .bg-field-insurance { width: 11rem; }
  .bg-field-status { width: 9rem; }
  .bg-field-code { width: 10rem; }
  .bg-field-professional { width: 9rem; }
  @media screen and (max-width: $size-md) {
    .cr-toolbar .cr-toolbar-field { flex: 1 1 45%; width: auto; }
  }
}
</style>
